<script setup lang="ts">
import type { ConfiguratorPool } from '~/models/configurator/configuratorPool.model'
import type { ConfiguratorPoolColor } from '~/models/configurator/configuratorPoolColor.model'
import type { ConfiguratorPoolLine } from '~/models/configurator/configuratorPoolLine.model'
import type { ConfiguratorPoolSize } from '~/models/configurator/configuratorPoolSize.model'
import type { ConfiguratorPoolStair } from '~/models/configurator/configuratorPoolStair.model'

interface Props {
  currentShownColors: ConfiguratorPoolColor[]
  currentSelectableColors: ConfiguratorPoolColor[]
  currentShownSizes: ConfiguratorPoolSize[]
  currentSelectableSizes: ConfiguratorPoolSize[]
  currentShownStairs: ConfiguratorPoolStair[]
  currentSelectableStairs: ConfiguratorPoolStair[]
  currentSize: ConfiguratorPoolSize

  currentPool: ConfiguratorPool | null
  currentPoolLine: ConfiguratorPoolLine
  poolLines: ConfiguratorPoolLine[]

  isFullyFilledIn: boolean

  formLabel: string
}

const props = defineProps<Props>()
const emits = defineEmits<{
  selectPoolLine: [ConfiguratorPoolLine]
  openForm: []
}>()
const { t } = useI18n()
const currentSize = defineModel<ConfiguratorPoolSize>('currentSize', {
  required: true,
})

const currentStair = defineModel<ConfiguratorPoolStair>('currentStair', {
  required: true,
})

const currentColor = defineModel<ConfiguratorPoolColor>('currentColor', {
  required: true,
})

type MobileStepId = 'size' | 'stair' | 'color' | 'quote' | 'line'
interface MobileStep {
  id: MobileStepId
  label: string
}

const mobileSteps: MobileStep[] = [
  {
    id: 'line',
    label: t('label.choose_pool_line'),
  },

  {
    id: 'size',
    label: t('label.choose_size'),
  },
  {
    id: 'stair',
    label: t('label.choose_stair'),
  },
  {
    id: 'color',
    label: t('label.choose_color'),
  },
  {
    id: 'quote',
    label: t('label.choose_quote'),
  },
] as const

const currentStepIndex = ref<number>(props.isFullyFilledIn ? mobileSteps.length - 1 : 0)

const currentStep = computed<MobileStep>(() => {
  return mobileSteps[currentStepIndex.value]
})

const nextMobileStep = computed<MobileStep | null>(() => {
  return mobileSteps[currentStepIndex.value + 1] || null
})

const previousMobileStep = computed<MobileStep | null>(() => {
  return mobileSteps[currentStepIndex.value - 1] || null
})

function goToNextStep() {
  if (nextMobileStep.value)
    currentStepIndex.value++
}

function goToPreviousStep() {
  if (previousMobileStep.value)
    currentStepIndex.value--
}

function onSelectPoolLine(poolLine: ConfiguratorPoolLine) {
  emits('selectPoolLine', poolLine)
}
</script>

<template>
  <div>
    <div class="mx-auto hidden justify-between container lg:grid lg:grid-cols-[0.3fr_1fr_0.3fr]">
      <ConfiguratorSizeSelector
        v-model="currentSize"
        :shown-sizes="currentShownSizes"
        :selectable-sizes="currentSelectableSizes"
      />

      <ConfiguratorStairSelector
        v-model="currentStair"
        :shown-stairs="currentShownStairs"
        :selectable-stairs="currentSelectableStairs"
      />

      <ConfiguratorColorSelector
        v-model="currentColor"
        :shown-colors="currentShownColors"
        :selectable-colors="currentSelectableColors"
      />
    </div>
    <div class="mx-auto flex flex-col items-center justify-between gap-8 container lg:hidden">
      <div class="grid grid-cols-[8rem_1fr_8rem] w-full justify-center text-center text-[1.6rem]">
        <button
          class="flex items-center justify-center gap-2 border-b-2 border-input py-2"
          @click="goToPreviousStep"
        >
          <IconArrowRight v-if="!!previousMobileStep" class="h-12 rotate-180 transform" />
        </button>

        <button
          class="flex flex-1 items-center justify-center gap-2 border-b-2 border-starline-primary py-2"
        >
          <p>
            {{ t('label.step') }} {{ `${currentStepIndex + 1}` }}: {{ currentStep.label }}
          </p>
        </button>

        <button
          class="flex items-center justify-center gap-2 border-b-2 border-input py-2"
          @click="goToNextStep"
        >
          <IconArrowRight v-if="!!nextMobileStep" class="h-12" />
        </button>
      </div>
      <div class="mx-auto w-full px-8 container">
        <ConfiguratorPoolLineSelectorMobile
          v-if="currentStep.id === 'line'"
          :current-pool-line="currentPoolLine"
          :pool-lines="poolLines"
          @select-pool-line="onSelectPoolLine"
        />

        <ConfiguratorSizeSelector
          v-else-if="currentStep.id === 'size'"
          v-model="currentSize"
          :shown-sizes="currentShownSizes"
          :selectable-sizes="currentSelectableSizes"
        />

        <ConfiguratorStairSelector
          v-else-if="currentStep.id === 'stair'"
          v-model="currentStair"
          :shown-stairs="currentShownStairs"
          :selectable-stairs="currentSelectableStairs"
        />

        <ConfiguratorColorSelector
          v-else-if="currentStep.id === 'color'"
          v-model="currentColor"
          :shown-colors="currentShownColors"
          :selectable-colors="currentSelectableColors"
        />
        <ConfiguratorQuoteSelector
          v-else-if="currentStep.id === 'quote'"
          v-model="currentColor"
          :form-label="props.formLabel"
          :current-pool="currentPool"
          :shown-colors="currentShownColors"
          :selectable-colors="currentSelectableColors"
          @open-form="$emit('openForm')"
        />
      </div>
    </div>
  </div>
</template>
